import * as React from 'react';
import * as PropTypes from 'prop-types';
import createRef from '@roo/create-react-ref/createRef';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import { guid } from '@utiljs/guid';
import { EditorProps, EditorInstance } from './interface';
import EditorBar from './EditorBar';

import 'react-quill/dist/quill.snow.css';

Quill.register('modules/imageResize', ImageResize);

class Editor extends React.Component<EditorProps, any> {
  static propsTypes = {
    className: PropTypes.string,
    readOnly: PropTypes.bool,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    uploadImage: PropTypes.func,
    linkModelPopupContainer: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.func]),
    maxLength: PropTypes.number,
    onExceed: PropTypes.func,
  }

  static defaultProps = {
    className: '',
    readOnly: false,
    value: undefined,
    placeholder: '',
    linkModelPopupContainer: undefined,
    maxLength: undefined,
    onChange: () => { },
    onFocus: () => { },
    onBlur: () => { },
    uploadImage: () => { },
    onExceed: () => { },
  }

  state = {
    currentLength: 0,
    isExceeded: false,
  }

  private value: string = '';

  private shortId = guid('roo-plus-editor-bar-');

  private quillRef = null;

  private reactQuillRef: React.RefObject<HTMLInputElement> = createRef();

  private modules = {
    toolbar: {
      container: `#${this.shortId}`,
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      handleStyles: {
        width: '10px',
        height: '10px',
        backgroundColor: '#3F4156',
        border: 'none',
        borderRadius: '5px',
      },
      modules: ['Resize', 'Toolbar'],
    },
  }

  private formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'color',
    'link',
    'image',
    'background',
    'align',
    'imagewithstyle',
    'alt',
    'width',
    'height',
    'style',
  ];

  imageHandler = () => {
    const { uploadImage } = this.props;
    const input = document.createElement('input');

    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = () => {
      if (uploadImage) {
        uploadImage(input.files![0], this.insertImage);
      }
    };
  }

  handleChange = (value: string, delta: object, source: string, editor: EditorInstance) => {
    const { onChange } = this.props;
    this.value = value;

    if (onChange) {
      onChange(value, delta, editor);
    }
  }

  handleFocus = (range: object, source: string, editor: EditorInstance) => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus(this.value, editor);
    }
  }

  handleBlur = (range: object, source: string, editor: EditorInstance) => {
    const { onBlur } = this.props;
    if (onBlur) {
      onBlur(this.value, editor);
    }
  }

  insertImage = (imgUrl: string) => {
    const range = this.quillRef.getSelection(true);
    this.quillRef.insertEmbed(range.index, 'image', imgUrl);
  }

  insertLink = (link: string) => {
    const range = this.quillRef.getSelection(true);
    const position = range ? range.index : 0;
    this.quillRef.clipboard.dangerouslyPasteHTML(position, link);
  }

  attachQuillRefs = () => {
    if (typeof this.reactQuillRef.current.getEditor !== 'function') return;
    this.quillRef = this.reactQuillRef.current.getEditor();
  }

  componentDidMount() {
    this.attachQuillRefs();
    this.quillRef.getModule('toolbar').addHandler('image', this.imageHandler);

    // 初始化字数统计
    if (this.props.value) {
      const textLength = this.quillRef.getText().length - 1;
      const isExceeded = this.props.maxLength !== undefined && textLength > this.props.maxLength;

      this.setState({
        currentLength: textLength,
        isExceeded,
      });
    }
  }

  componentDidUpdate(prevProps: EditorProps) {
    // 当 value 从外部更新时，更新字数统计
    if (prevProps.value !== this.props.value && this.quillRef) {
      const textLength = this.quillRef.getText().length - 1;
      const isExceeded = this.props.maxLength !== undefined && textLength > this.props.maxLength;
      this.setState({
        currentLength: textLength,
        isExceeded,
      });
    }
  }

  render() {
    const {
      value,
      readOnly,
      placeholder,
      className,
      linkModelPopupContainer,
      maxLength,
    } = this.props;

    const { currentLength, isExceeded } = this.state;

    // 根据是否超出字数限制添加错误样式
    const editorClassName = `roo-plus-editor ${className} ${isExceeded ? 'roo-plus-editor-exceeded' : ''}`;

    return (
      <div className={editorClassName}>
        <EditorBar
          linkModelPopupContainer={linkModelPopupContainer}
          shortId={this.shortId}
          insertLink={this.insertLink}
        />
        <ReactQuill
          ref={this.reactQuillRef}
          theme="snow"
          className={className}
          value={value}
          readOnly={readOnly}
          placeholder={placeholder}
          modules={this.modules}
          formats={this.formats}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        />
        {maxLength !== undefined && (
          <div className={`roo-plus-editor-counter ${isExceeded ? 'roo-plus-editor-counter-exceeded' : ''}`}>
            {currentLength}
            /
            {maxLength}
          </div>
        )}
      </div>
    );
  }
}

export default Editor;
